import { useState } from 'react'
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  TextField,
  Button,
  useTheme,
  Snackbar,
  Alert,
  MenuItem,
} from '@mui/material'
import { 
  Email, 
  Send,
  LinkedIn,
  Instagram,
} from '@mui/icons-material'
import { images } from '../config/images'

const Contact: React.FC = () => {
  const theme = useTheme()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'general',
    message: '',
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setIsSubmitting(true)
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || undefined,
          message: `Subject: ${subjects.find(s => s.value === formData.subject)?.label || formData.subject}\n\n${formData.message}`,
        }),
      })

      if (response.ok) {
        setShowSuccess(true)
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: 'general',
          message: '',
        })
      } else {
        const error = await response.json()
        console.error('Failed to send message:', error)
        setShowError(true)
      }
    } catch (error) {
      console.error('Network error:', error)
      setShowError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const subjects = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'sponsorship', label: 'Sponsorship' },
    { value: 'collaboration', label: 'Collaboration' }
  ]

  const contactInfo = [
    {
      icon: <Email sx={{ fontSize: 32, color: theme.palette.primary.main }} />,
      title: 'Email Us',
      info: 'hello@propulsion.industries',
      description: 'General inquiries and information',
    },
    /*{
      icon: <Business sx={{ fontSize: 32, color: theme.palette.primary.main }} />,
      title: 'Business Inquiries',
      info: 'business@propulsionindustries.com',
      description: 'Partnerships and collaborations',
    },
    {
      icon: <Phone sx={{ fontSize: 32, color: theme.palette.primary.main }} />,
      title: 'Phone',
      info: '+52 55 1234 5678',
      description: 'Monday to Friday, 9 AM - 6 PM (CST)',
    },
    {
      icon: <LocationOn sx={{ fontSize: 32, color: theme.palette.primary.main }} />,
      title: 'Location',
      info: 'Ciudad Universitaria, Mexico City',
      description: 'Universidad Nacional Autónoma de México',
    },*/
  ]

  /*const offices = [
    {
      city: 'Mexico City',
      country: 'Mexico',
      address: 'Circuito Exterior S/N, Ciudad Universitaria, Coyoacán, 04510',
      type: 'Headquarters & Research Lab',
      emoji: '🏢',
    },
    {
      city: 'São Paulo',
      country: 'Brazil',
      address: 'Av. Prof. Luciano Gualberto, 380 - Butantã',
      type: 'Competition Operations',
      emoji: '🚀',
    },
    {
      city: 'Monterrey',
      country: 'Mexico',
      address: 'Av. Eugenio Garza Sada 2501 Sur, Tecnológico',
      type: 'Manufacturing Facility',
      emoji: '🏭',
    },
  ]*/

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${images.backgrounds.contactHero}) center/cover`,
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '3rem', md: '4.5rem' },
              fontWeight: 800,
              mb: 3,
            }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              maxWidth: '800px',
              mx: 'auto',
              opacity: 0.9,
              lineHeight: 1.6,
            }}
          >
            Ready to launch your ideas with us? Let's start a conversation about the future of rocketry
          </Typography>
        </Container>
      </Box>

      {/* Contact Form & Info */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={8}>
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 4, borderRadius: 3 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 3,
                }}
              >
                Send us a message
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company/Organization"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      variant="outlined"
                    >
                      {subjects.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Tell us about your project, collaboration ideas, or any questions you have..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isSubmitting}
                      startIcon={<Send />}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {contactInfo.map((contact, index) => (
                <Card
                  key={index}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.primary.main}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(255,0,0,0.15)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {contact.icon}
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {contact.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          mb: 1,
                        }}
                      >
                        {contact.info}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.description}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>

            {/* Social Media */}
            <Card sx={{ p: 3, mt: 3, borderRadius: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Follow Us
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  component="a"
                  href="https://www.linkedin.com/company/propulsion-industries"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    minWidth: 'auto',
                    p: 1.5,
                    borderRadius: '50%',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  <LinkedIn />
                </Button>
                <Button
                  variant="outlined"
                  component="a"
                  href="https://www.instagram.com/propulsion_industries/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    minWidth: 'auto',
                    p: 1.5,
                    borderRadius: '50%',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  <Instagram />
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Office Locations */}
      {/*<Box sx={{ bgcolor: '#f8f9fa', py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 8,
            }}
          >
            Our Locations
          </Typography>
          <Grid container spacing={4}>
            {offices.map((office, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(255,0,0,0.2)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '3rem', mb: 2 }}>
                    {office.emoji}
                  </Typography>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 700 }}
                  >
                    {office.city}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {office.type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {office.address}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {office.country}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>*/}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Message sent successfully! We'll get back to you within 24 hours.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          Failed to send message. Please try again later or contact us directly at hello@propulsion.industries
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Contact
